import React, { useState } from 'react';
import { Badge, Box, IconButton, Tooltip } from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  // v4 Chip, as the Weekly Report's filter chip — color="primary" there is
  // the app theme's navy, where the v5 one would be MUI's default blue
  Chip,
} from '@material-ui/core';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import FilterIcon from '../../global/FilterIcon';
import { TOKENS } from '../theme';

// The range Employee Sessions opens on: the last 30 days, through today.
export function defaultFromDate() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  d.setHours(0, 0, 0, 0);
  return d;
}
export function defaultToDate() {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

const sameDay = (a, b) =>
  !!a && !!b && new Date(a).toDateString() === new Date(b).toDateString();

// A range other than the default one is applied
export function isRangeFiltered(fromDate, toDate) {
  return (
    !sameDay(fromDate, defaultFromDate()) || !sameDay(toDate, defaultToDate())
  );
}

const fmtDay = (d, withYear) =>
  new Date(d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
  });

// "Filter applied" chip — click reopens the filter (via onClick), × resets
export function DateRangeChip({ fromDate, toDate, onClick, onReset }) {
  if (!isRangeFiltered(fromDate, toDate)) return null;
  return (
    <Chip
      size="small"
      color="primary"
      label={`${fmtDay(fromDate)} – ${fmtDay(toDate, true)}`}
      onClick={onClick}
      onDelete={onReset}
      title="Filter applied — click to change, x to go back to the last 30 days"
      style={{ justifySelf: 'start' }}
    />
  );
}

// Filter icon + From/To date-range dialog (modelled on MasterReadingTable).
// Shared by the By Employee and By Day tabs of the Employee Sessions root.
// `openRef` (optional) receives the open function, so a chip elsewhere in
// the row can reopen the same dialog.
export default function DateRangeFilter({
  fromDate,
  toDate,
  onDateChange,
  openRef,
}) {
  const [filterOpen, setFilterOpen] = useState(false);
  // Buffer the picker state so Cancel reverts cleanly.
  const [draftFrom, setDraftFrom] = useState(fromDate);
  const [draftTo, setDraftTo] = useState(toDate);

  const openFilter = () => {
    setDraftFrom(fromDate);
    setDraftTo(toDate);
    setFilterOpen(true);
  };
  if (openRef) openRef.current = openFilter;
  const applyFilter = () => {
    if (draftFrom) onDateChange('fromDate', draftFrom);
    if (draftTo) onDateChange('toDate', draftTo);
    setFilterOpen(false);
  };

  return (
    <React.Fragment>
      <Tooltip title="Filter by date">
        <IconButton
          onClick={openFilter}
          size="small"
          sx={{ border: `1px solid ${TOKENS.slate200}`, borderRadius: 1.5 }}
        >
          <Badge
            color="primary"
            variant="dot"
            invisible={!isRangeFiltered(fromDate, toDate)}
            overlap="circular"
          >
            <FilterIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Dialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        aria-labelledby="employee-filter-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="employee-filter-dialog">Filters</DialogTitle>
        <DialogContent>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            {/* xs: stack vertically (narrow phones); sm+: side-by-side on one row */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                pt: 1,
                flexDirection: { xs: 'column', sm: 'row' },
                '& > *': { flex: 1, minWidth: 0 },
              }}
            >
              <KeyboardDatePicker
                autoOk
                fullWidth
                variant="inline"
                inputVariant="outlined"
                label="From Date"
                format="yyyy-MM-dd"
                value={draftFrom}
                onChange={(d) => {
                  if (!d || d.toString() === 'Invalid Date') return;
                  setDraftFrom(new Date(d));
                }}
              />
              <KeyboardDatePicker
                autoOk
                fullWidth
                variant="inline"
                inputVariant="outlined"
                label="To Date"
                format="yyyy-MM-dd"
                value={draftTo}
                onChange={(d) => {
                  if (!d || d.toString() === 'Invalid Date') return;
                  setDraftTo(new Date(d));
                }}
              />
            </Box>
          </MuiPickersUtilsProvider>
        </DialogContent>
        <DialogActions sx={{ pr: 2, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setFilterOpen(false)}
            style={{
              width: '100px',
              marginRight: '16px',
              marginBottom: '16px',
            }}
          >
            Cancel
          </Button>
          <Button
            autoFocus
            variant="contained"
            onClick={applyFilter}
            color="primary"
            style={{
              width: '100px',
              marginRight: '16px',
              marginBottom: '16px',
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
